import {FormControl, InputLabel, MenuItem, Select, Grid, Typography} from '@material-ui/core';
import React from 'react';
import {useDispatch} from 'react-redux';
import './ProductRiders.sass';
import * as RI from 'ApplicationEntry/models/ProductsRiders/RiderInformation';

type VariableCodeRiderInformationProps = {
  changeRiderInformationAction: (amount: number, code: string, index: number) => void;
  removeRiderAction?: (index: number) => void;
  riderInformation: RI.RiderInformation;
  isDisabled?: ((code: string) => boolean) | boolean;
  title?: string;
  identifier: string;
  amountLabel?: string;
  codeLabel?: string;
  indexAmounts?: boolean;
  indexCodes?: boolean;
  enableSuccessively?: boolean;
  disjointCodeSet?: string[];
  showConsolidatedRiderSection?: boolean;
};

const VariableCodeRiderInformation = ({
  changeRiderInformationAction,
  removeRiderAction,
  riderInformation,
  isDisabled,
  title,
  identifier,
  amountLabel,
  codeLabel,
  indexAmounts,
  indexCodes,
  enableSuccessively,
  disjointCodeSet,
  showConsolidatedRiderSection,
}: VariableCodeRiderInformationProps) => {
  const dispatch = useDispatch();
  const numberRiderCodes = RI.isLoaded(riderInformation)
    ? disjointCodeSet !== undefined
      ? riderInformation.Data.riderData.length - (disjointCodeSet.length - 1)
      : riderInformation.Data.riderData.length
    : 0;
  const riderCodeList = RI.isLoaded(riderInformation)
    ? riderInformation.Data.riderData.slice(0, numberRiderCodes)
    : ([] as RI.RiderCode[]);
  const getSelectedRiderCodeValue = (index: number) =>
    RI.isLoaded(riderInformation) ? riderInformation.Data.codes.get(index) : '';
  const getSelectedRiderAmountValue = (index: number) =>
    RI.isLoaded(riderInformation) ? riderInformation.Data.amounts.get(index) : Number.NaN;
  const getRiderAmountColumns = (outerIndex: number, numberRiderCodes: number, riderCodeList: RI.RiderCode[]) => {
    const start = outerIndex * 4;
    const end = Math.min(outerIndex * 4 + 4, numberRiderCodes);

    let elements = [] as JSX.Element[];

    for (let i = start; i < end; i++) {
      elements.push(displayRiderAmounts(riderCodeList[i], i));
    }

    return elements;
  };
  const getRiderAmountRows = (numberRiderCodes: number, riderCodeList: RI.RiderCode[]) => {
    let rowDivisionElements = [] as JSX.Element[];

    const rowCount = numberRiderCodes / 4;

    for (let i = 0; i < rowCount; i++) {
      const rowDivisionElement = (
        <Grid container spacing={4} key={`${identifier}-rider-row-${i}`}>
          {getRiderAmountColumns(i, numberRiderCodes, riderCodeList)}
        </Grid>
      );

      rowDivisionElements.push(rowDivisionElement);
    }

    return rowDivisionElements;
  };
  const getRiderCodeColumns = (outerIndex: number, numberRiderCodes: number, riderCodeList: RI.RiderCode[]) => {
    const start = outerIndex * 4;
    const end = Math.min(outerIndex * 4 + 4, numberRiderCodes);

    let elements = [] as JSX.Element[];

    for (let i = start; i < end; i++) {
      elements.push(displayRiderCodes(riderCodeList[i], i));
    }

    return elements;
  };
  const getRiderCodeRows = (numberRiderCodes: number, riderCodeList: RI.RiderCode[]) => {
    let rowDivisionElements = [] as JSX.Element[];

    const rowCount = numberRiderCodes / 4;

    for (let i = 0; i < rowCount; i++) {
      const rowDivisionElement = (
        <Grid container spacing={4} key={`${identifier}-rider-row-${i}`}>
          {getRiderCodeColumns(i, numberRiderCodes, riderCodeList)}
        </Grid>
      );

      rowDivisionElements.push(rowDivisionElement);
    }

    return rowDivisionElements;
  };
  const getHeader = (header: string) => {
    return (
      <div className="critical-illness-section__title-container">
        <Typography variant="subtitle1" className="critical-illness-section__sub-title-container-title">
          {header}
        </Typography>
      </div>
    );
  };

  const disableRider = (index: number) => !(index === 0 || getSelectedRiderCodeValue(index - 1) !== undefined);
  const displayRiderCodes = (rider: RI.RiderCode, index: number) => {
    if (!showConsolidatedRiderSection) {
      return (
        <>
          <Grid item xs={12} md={3}>
            <FormControl
              variant="outlined"
              className="product-riders-section__policy-type__form__row--field"
              key={`code-${rider.code}`}
            >
              <InputLabel
                shrink
                id={`rider-code-label-${index}`}
                className={'product-riders-section__policy-type__form__row--label'}
              >
                {codeLabel ? codeLabel : 'Rider Code'}
                {indexCodes ? ` ${index + 1}` : ''}
              </InputLabel>
              <Select
                labelId={`label-rider-code-${index}`}
                id={`rider-code-${index}`}
                value={getSelectedRiderCodeValue(index) === undefined ? '' : getSelectedRiderCodeValue(index)}
                onChange={event =>
                  event.target.value === ''
                    ? dispatch(removeRiderAction!(index))
                    : dispatch(
                        changeRiderInformationAction(
                          getSelectedRiderAmountValue(index)!,
                          event.target.value as string,
                          index
                        )
                      )
                }
                displayEmpty
                disabled={
                  enableSuccessively
                    ? disableRider(index) &&
                      !(typeof isDisabled === 'boolean'
                        ? isDisabled
                        : isDisabled !== undefined
                        ? isDisabled(rider.code)
                        : false)
                    : typeof isDisabled === 'boolean'
                    ? isDisabled
                    : isDisabled !== undefined
                    ? isDisabled(rider.code)
                    : false
                }
              >
                <MenuItem value="" key={`${identifier}-code-none`}>
                  None
                </MenuItem>
                {RI.isLoaded(riderInformation) ? (
                  riderInformation.Data.getAvailableRiderCodesByCode(
                    getSelectedRiderCodeValue(index)!,
                    disjointCodeSet!
                  ).map((code, index) => (
                    <MenuItem value={code.code} key={`${identifier}-code-${index}`}>
                      {code.code}
                    </MenuItem>
                  ))
                ) : (
                  <></>
                )}
              </Select>
            </FormControl>
          </Grid>
        </>
      );
    } else {
      return <></>;
    }
  };
  const displayRiderAmounts = (rider: RI.RiderCode, index: number) => {
    return (
      <>
        <Grid item xs={12} md={3}>
          <FormControl
            variant="outlined"
            className="product-riders-section__policy-type__form__row--field"
            key={`amount-${rider.code}`}
          >
            <InputLabel
              shrink
              id={`rider-amount-label-${index}`}
              className={'product-riders-section__policy-type__form__row--label'}
            >
              {amountLabel ? rider.code : 'Units'}
              {indexAmounts ? ` ${index + 1}` : ''}
            </InputLabel>
            <Select
              labelId={`label-rider-amount-${index}`}
              id={`rider-amount-${index}`}
              value={
                getSelectedRiderAmountValue(index) === undefined || getSelectedRiderAmountValue(index) === Number.NaN
                  ? ''
                  : getSelectedRiderAmountValue(index)
              }
              onChange={event =>
                dispatch(
                  changeRiderInformationAction(
                    event.target.value === '' ? Number.NaN : (event.target.value as number),
                    !showConsolidatedRiderSection ? getSelectedRiderCodeValue(index)! : rider.code!,
                    index
                  )
                )
              }
              displayEmpty
              disabled={
                enableSuccessively && !showConsolidatedRiderSection
                  ? getSelectedRiderCodeValue(index) === undefined &&
                    !(typeof isDisabled === 'boolean'
                      ? isDisabled
                      : isDisabled !== undefined
                      ? isDisabled(rider.code)
                      : false)
                  : typeof isDisabled === 'boolean'
                  ? isDisabled
                  : isDisabled !== undefined
                  ? isDisabled(rider.code)
                  : false
              }
            >
              <MenuItem value="" key={`${identifier}-unit-none`}>
                None
              </MenuItem>
              {RI.isLoaded(riderInformation) ? (
                riderInformation.Data.getAvailableRiderUnitsByCode(
                  !showConsolidatedRiderSection ? getSelectedRiderCodeValue(index)! : rider.code!
                ).map((unit, index) => (
                  <MenuItem value={unit} key={`${identifier}-unit-${index}`}>
                    {unit}
                  </MenuItem>
                ))
              ) : (
                <></>
              )}
            </Select>
          </FormControl>
        </Grid>
      </>
    );
  };

  return (
    <>
      {getHeader(title ? (title === 'Riders' ? '' : title) : 'Rider Code')}
      {getRiderCodeRows(numberRiderCodes, riderCodeList)}
      {getHeader(title ? title : 'Rider Amount')}
      {getRiderAmountRows(numberRiderCodes, riderCodeList)}
    </>
  );
};

export default VariableCodeRiderInformation;
